import { z } from 'zod';

/** Metadata'dan gelen alan tanımı (kayıt formu / ayar formu) */
export interface SchemaField {
  field: string;
  caption?: string;
  readonly?: boolean;
  column: { type: string; maxLength: number | null; nullable: boolean } | null;
  required?: { mesaj: string; sifirBos: boolean } | null;
}

const INT = new Set(['int', 'bigint', 'smallint', 'tinyint']);
const NUM = new Set(['float', 'real', 'decimal', 'numeric', 'money', 'smallmoney']);
const DATE = new Set(['datetime', 'smalldatetime', 'date', 'datetime2']);
const TEXT = new Set(['char', 'nchar', 'varchar', 'nvarchar', 'text', 'ntext']);
const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?Z?$/;

/**
 * Kolon tipi, uzunluk ve zorunlu alan kurallarından Zod şeması üretir (React Hook Form çözümleyicisi için).
 * Sunucu aynı kuralları yeniden uygular; istemci şeması kullanıcıya erken ve alan bazlı geri bildirim içindir.
 */
export function buildRowSchema(fields: SchemaField[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of fields) {
    if (f.readonly) continue;
    const label = f.caption ?? f.field;
    const t = f.column?.type ?? '';
    let s: z.ZodTypeAny;
    if (INT.has(t)) s = z.union([z.number().int(`${label}: tam sayı olmalı`), z.null()]);
    else if (NUM.has(t)) s = z.union([z.number().refine(Number.isFinite, `${label}: geçerli bir sayı giriniz`), z.null()]);
    else if (DATE.has(t)) s = z.union([z.string().regex(ISO, `${label}: geçerli bir tarih giriniz`), z.null()]);
    else if (t === 'bit') s = z.union([z.boolean(), z.number(), z.null()]);
    else if (TEXT.has(t) && f.column?.maxLength && f.column.maxLength > 0) s = z.union([z.string().max(f.column.maxLength, `${label}: en fazla ${f.column.maxLength} karakter`), z.null()]);
    else s = z.any();
    if (f.required) {
      const r = f.required;
      // zorunlu alan: undefined (yeni kayıtta hiç girilmemiş) da boş sayılır → optional() kullanılmaz
      shape[f.field] = z.preprocess((v) => (v === undefined ? null : v), z.any().refine((v) => !(v == null || (typeof v === 'string' && !v.trim()) || (r.sifirBos && Number(v) === 0)), r.mesaj).pipe(s));
      continue;
    }
    shape[f.field] = s.optional();
  }
  return z.object(shape).passthrough();
}

/** İlk doğrulama hatası: alan + mesaj */
export function firstIssue(errors: Record<string, any>): { field: string; message: string } | null {
  const k = Object.keys(errors)[0];
  return k ? { field: k, message: String(errors[k]?.message ?? 'Geçersiz değer') } : null;
}
